import { PublicClientApplication } from '@azure/msal-browser';
import type { AppConfig } from '@/config/loadConfig';
import { buildMsalConfig } from './msalConfig';

export interface MsalInit {
  pca: PublicClientApplication;
  /**
   * Router path to restore after a login redirect round-trip (carried via the
   * OAuth `state` parameter), or null when this page load is not a redirect return.
   */
  redirectPath: string | null;
  /**
   * Error message when the redirect return carried an AAD error (e.g. the user
   * picked an account from a tenant where the app has no service principal —
   * AADSTS500011). The app must still render (signed out) so the user can retry;
   * a failed sign-in is a user-recoverable state, not a bootstrap failure.
   */
  redirectError: string | null;
}

/**
 * The OAuth `state` round-trips through AAD as an opaque string — trust it only
 * as an in-app router path (a crafted authorize URL can carry any value).
 * Anything that isn't a plain absolute path (absolute URL, protocol-relative
 * `//host`, empty) is dropped rather than navigated.
 */
function sanitizeStatePath(state: string | undefined): string | null {
  if (typeof state !== 'string') return null;
  if (!state.startsWith('/') || state.startsWith('//')) return null;
  return state;
}

/**
 * Creates and initializes the MSAL PublicClientApplication. Must complete before
 * the first render: msal-browser v3+ requires initialize() before any other call,
 * and handleRedirectPromise() must run on every page load to finish a pending
 * redirect flow (it resolves null when there is none).
 */
export async function createMsal(config: AppConfig): Promise<MsalInit> {
  const pca = new PublicClientApplication(buildMsalConfig(config));
  await pca.initialize();

  let redirectPath: string | null = null;
  let redirectError: string | null = null;

  try {
    const result = await pca.handleRedirectPromise();
    if (result?.account) {
      pca.setActiveAccount(result.account);
      redirectPath = sanitizeStatePath(result.state);
    }
  } catch (error) {
    // AAD returned an error on the redirect (wrong-tenant account, consent
    // declined, CA block, ...). Swallow into a reportable value — the caller
    // renders the app unauthenticated and the user can retry from Landing.
    redirectError = error instanceof Error ? error.message : String(error);
  }

  if (!pca.getActiveAccount()) {
    // Not a (successful) redirect return — adopt a cached account if one exists.
    const existing = pca.getAllAccounts()[0];
    if (existing) {
      pca.setActiveAccount(existing);
    }
  }

  return { pca, redirectPath, redirectError };
}
